// A client's place and property in parts (Street 1, Street 2, City, State, ZIP), shared with the quote form.
import { type PropertyInput, type PropertyRow, stateCode } from "@steward/shared";
import { FormField, InputCount, InputText } from "../../components/fields";

export interface PropertyDraft {
  label: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  beds: string;
  baths: string;
  rooms: string;
  sqft: string;
  lawnSqft: string;
  accessNotes: string;
}

export const EMPTY_PROPERTY: PropertyDraft = {
  label: "",
  address: "",
  city: "New Orleans",
  state: "LA",
  zip: "",
  beds: "",
  baths: "",
  rooms: "",
  sqft: "",
  lawnSqft: "",
  accessNotes: "",
};

const num = (s: string) => (s.trim() && Number.isFinite(Number(s)) ? Number(s) : null);

export const txt = (s: string) => s.trim() || undefined;

export function propertyOut(p: PropertyDraft) {
  return {
    label: txt(p.label),
    address: p.address.trim(),
    city: txt(p.city),
    state: txt(p.state.toUpperCase()),
    zip: txt(p.zip),
    beds: num(p.beds),
    baths: num(p.baths),
    rooms: num(p.rooms),
    sqft: num(p.sqft),
    lawnSqft: num(p.lawnSqft),
    accessNotes: txt(p.accessNotes),
  };
}

/** What a new client still needs, in words, or null when it can be saved. Last name and email are asked for but not a must. */
export function newClientMissing(f: { firstName: string; phone: string }, p: PropertyDraft): string | null {
  const need = [
    f.firstName.trim() ? null : "first name",
    f.phone.trim() ? null : "phone",
    p.address.trim() ? null : "street address",
  ].filter((x): x is string => x !== null);
  if (!need.length) return null;
  const last = need.pop() ?? "";
  return `A new client needs a ${need.length ? `${need.join(", ")} and ${last}` : last}.`;
}

/** Jobber's address box: one field per line with the browser's autofill names, plus the place's size folded below. */
export interface PlaceDraft {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  beds: string;
  baths: string;
  rooms: string;
  sqft: string;
  lawnSqft: string;
  codes: string;
}

export const EMPTY_PLACE: PlaceDraft = {
  street1: "",
  street2: "",
  city: EMPTY_PROPERTY.city,
  state: EMPTY_PROPERTY.state,
  zip: "",
  country: "United States",
  beds: "",
  baths: "",
  rooms: "",
  sqft: "",
  lawnSqft: "",
  codes: "",
};

/** The browser fills "LOUISIANA" or "Louisiana"; the record keeps the 2 letter code. A typo keeps its first 2 letters. */
const stateOf = (s: string) => stateCode(s) || s.trim().toUpperCase().slice(0, 2);

/** The address box as the property the server takes (Street 2 joins Street 1, state names become the 2 letter code). */
export function placeProperty(p: PlaceDraft): PropertyDraft {
  return {
    ...EMPTY_PROPERTY,
    address: [p.street1.trim(), p.street2.trim()].filter(Boolean).join(", "),
    city: p.city,
    state: stateOf(p.state),
    zip: p.zip,
    beds: p.beds,
    baths: p.baths,
    rooms: p.rooms,
    sqft: p.sqft,
    lawnSqft: p.lawnSqft,
    accessNotes: p.codes,
  };
}

/** A saved property back in the address box. Street 2 was joined to Street 1 with a comma, so it splits there again. */
export function placeOf(p: PropertyRow): PlaceDraft {
  const cut = p.address.indexOf(", ");
  const n = (v: number | null) => (v === null ? "" : String(v));
  return {
    ...EMPTY_PLACE,
    street1: cut < 0 ? p.address : p.address.slice(0, cut),
    street2: cut < 0 ? "" : p.address.slice(cut + 2),
    city: p.city ?? "",
    state: p.state ?? "",
    zip: p.zip ?? "",
    beds: n(p.beds),
    baths: n(p.baths),
    rooms: n(p.rooms),
    sqft: n(p.sqft),
    lawnSqft: n(p.lawnSqft),
    codes: p.accessNotes ?? "",
  };
}

/** The address box as the property to save. A box emptied on a saved property clears it instead of keeping the old words. */
export function placeOut(place: PlaceDraft, label: string, old: PropertyRow | null): PropertyInput {
  const out: PropertyInput = propertyOut({ ...placeProperty(place), label });
  if (old) {
    for (const k of ["label", "city", "state", "zip", "accessNotes"] as const) if (old[k] && !out[k]) out[k] = null;
  }
  return out;
}

/** The address box's fields. Goes inside a `client-form` grid, inside a real form so Chrome fills it. */
export function PlaceFields({
  p,
  set,
  trade,
  business,
  optional,
}: {
  p: PlaceDraft;
  set: (p: PlaceDraft) => void;
  trade: string;
  business: boolean;
  /** A saved client with no address yet may be saved without one. */
  optional?: boolean;
}) {
  const f = (k: keyof PlaceDraft) => ({
    value: p[k],
    onChange: (e: { target: { value: string } }) => set({ ...p, [k]: e.target.value }),
  });
  const count = (k: "beds" | "baths" | "rooms") => ({ value: p[k], onChange: (v: string) => set({ ...p, [k]: v }) });
  const sized = trade === "cleaning" || trade === "lawn";
  const summary = [
    p.beds && `${p.beds} bed`,
    p.baths && `${p.baths} bath`,
    p.rooms && (business ? `${p.rooms} rooms` : `${p.rooms} other ${p.rooms === "1" ? "room" : "rooms"}`),
    p.sqft && `${p.sqft} sq ft`,
    p.lawnSqft && `${p.lawnSqft} sq ft lawn`,
    p.codes && "codes saved",
  ].filter(Boolean);
  return (
    <>
      <div className="span-6">
        <InputText label="Street 1" name="address-line1" autoComplete="address-line1" required={!optional} {...f("street1")} />
      </div>
      <div className="span-6">
        <InputText
          label="Street 2"
          name="address-line2"
          autoComplete="address-line2"
          placeholder="Apartment, suite, unit"
          {...f("street2")}
        />
      </div>
      <div className="span-3 wide-phone">
        <InputText label="City" name="address-level2" autoComplete="address-level2" {...f("city")} />
      </div>
      <div className="span-3">
        <InputText
          label="State"
          name="address-level1"
          autoComplete="address-level1"
          {...f("state")}
          onBlur={() => set({ ...p, state: stateOf(p.state) })}
        />
      </div>
      <div className="span-3">
        <InputText label="ZIP code" name="postal-code" autoComplete="postal-code" inputMode="numeric" {...f("zip")} />
      </div>
      <div className="span-3 wide-phone">
        <FormField label="Country">
          <select className="select" name="country" autoComplete="country-name" {...f("country")}>
            <option>United States</option>
          </select>
        </FormField>
      </div>
      <details className="span-6 fold">
        <summary>
          {sized ? "Size and gate codes" : "Gate and alarm codes"}
          {summary.length ? <span className="muted"> {summary.join(", ")}</span> : null}
        </summary>
        <div className="form-grid">
          {trade === "cleaning" ? (
            business ? (
              <>
                <InputCount label="Rooms" max={500} {...count("rooms")} />
                <InputText label="Square feet" inputMode="numeric" autoComplete="off" {...f("sqft")} />
              </>
            ) : (
              <>
                <InputCount label="Bedrooms" max={20} {...count("beds")} />
                <InputCount label="Bathrooms" max={20} decimal {...count("baths")} />
                <InputCount label="Other rooms" max={50} {...count("rooms")} />
                <InputText label="Square feet" inputMode="numeric" autoComplete="off" {...f("sqft")} />
              </>
            )
          ) : trade === "lawn" ? (
            <InputText label="Lawn size" suffix="sq ft" inputMode="numeric" autoComplete="off" {...f("lawnSqft")} />
          ) : null}
          <InputText
            label="Gate and alarm codes"
            wide
            autoComplete="off"
            description="Only the office sees these."
            {...f("codes")}
          />
        </div>
      </details>
    </>
  );
}

/** One part of a long form, Jobber style: the title and a line on the left, the fields on the right. */
